import { type ReactNode, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../Icon/Icon'
import styles from './Modal.module.scss'

interface ModalProps {
  children: ReactNode
  labelledBy?: string
  onClose: () => void
}

export const Modal = ({ children, labelledBy, onClose }: ModalProps) => {
  const { t } = useTranslation()
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    document.body.classList.add('no-scroll')
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.classList.remove('no-scroll')
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose])

  return (
    <div
      ref={overlayRef}
      className={styles.overlay}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === overlayRef.current) {
          onClose()
        }
      }}
    >
      <div aria-modal aria-labelledby={labelledBy} className={styles.body} role="dialog">
        <button aria-label={t('common.close')} className={styles.close} type="button" onClick={onClose}>
          <Icon name="close" size={24} />
        </button>
        {children}
      </div>
    </div>
  )
}
